import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Input,
  Select,
  Switch,
  InputNumber,
  Button,
  Table,
  Space,
  Divider,
  Typography,
  Popconfirm,
  message,
  Spin,
  Modal,
  Form,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import { useTheme } from '../../context/ThemeContext';
import { trIncludes } from '../../utils/trText';

const { Title, Text } = Typography;
const { Option } = Select;

const defaultElement = (index = 0) => ({
  name: '',
  type: 1,
  required: false,
  placeholder: '',
  min: null,
  max: null,
  value: '',
  query: '',
  class: '',
  attr: '',
  w: 6,
  default_value: '',
  active: true,
  sira: index,
});

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const FormManager = ({ formId: propFormId }) => {
  const { darkMode } = useTheme();
  const query = useQuery();
  const params = useParams();
  const formId = useMemo(() => {
    const q = query.get('id');
    const routeId = params?.id ? Number(params.id) : undefined;
    return propFormId || (q ? Number(q) : undefined) || routeId;
  }, [propFormId, params, query]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [elementTypes, setElementTypes] = useState([]);
  const [formMeta, setFormMeta] = useState({
    form_name: '',
    name: '',
    target: '',
    table_name: '',
    submittext: 'Kaydet',
    function: 'Insert',
    redirect: '',
    view: 0,
    yetki: '',
  });
  const [elements, setElements] = useState([]);
  const [interact, setInteract] = useState({});
  const [manualFormId, setManualFormId] = useState(formId || null);
  const [createFormModalVisible, setCreateFormModalVisible] = useState(false);
  const [creatingForm, setCreatingForm] = useState(false);
  const [createFormForm] = Form.useForm();

  const loadElementTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/form/element-types`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setElementTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Element types load error', err);
      setElementTypes([]);
    }
  }, []);

  const loadForm = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/form/formlar/${id}/raw`);
      if (!res.ok) {
        if (res.status === 404) {
          setFormMeta((m) => ({ ...m }));
          setElements([defaultElement(0)]);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setFormMeta({
        form_name: data?.form_name || data?.name || '',
        name: data?.name || '',
        target: data?.target || '',
        table_name: data?.table_name || '',
        submittext: data?.submittext || 'Kaydet',
        function: data?.function || 'Insert',
        redirect: data?.redirect || '',
        view: data?.view ?? 0,
        yetki: data?.yetki || '',
      });
      const elems = Array.isArray(data?.elements) ? data.elements.map((el, i) => ({
        ...defaultElement(i),
        ...el,
        sira: el?.sira ?? i,
      })) : [defaultElement(0)];
      elems.sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));
      setElements(elems);
      setInteract(data?.interact || {});
    } catch (err) {
      console.error('Form load error', err);
      message.error('Form yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadElementTypes();
  }, [loadElementTypes]);

  useEffect(() => {
    if (formId) {
      loadForm(formId);
    }
    setManualFormId(formId || null);
  }, [formId, loadForm]);

  const handleMetaChange = (key, value) => {
    setFormMeta((prev) => ({ ...prev, [key]: value }));
  };

  const updateElement = (index, key, value) => {
    setElements((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const moveElement = (index, direction) => {
    setElements((prev) => {
      const next = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      const temp = next[index];
      next[index] = next[newIndex];
      next[newIndex] = temp;
      return next.map((el, i) => ({ ...el, sira: i }));
    });
  };

  const addElement = () => {
    setElements((prev) => {
      const next = [...prev, defaultElement(prev.length)];
      return next;
    });
  };

  const addFormElement = () => {
    setCreateFormModalVisible(true);
    createFormForm.resetFields();
  };

  const createNewForm = async () => {
    try {
      const values = await createFormForm.validateFields();
      setCreatingForm(true);
      const newFormId = Number(values.mysql_id);
      const newForm = {
        mysql_id: newFormId,
        form_name: values.form_name || '',
        name: values.name || '',
        target: values.target || 'formHandle',
        table_name: values.table_name || '',
        submittext: values.submittext || 'Kaydet',
        function: values.function || 'Insert',
        redirect: values.redirect || '',
        yetki: values.yetki || '',
        view: values.view ?? 1,
        elements: [],
      };

      const res = await fetch(`${API_BASE_URL}/form/formlar/${newFormId}/raw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newForm),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${res.status}`);
      }

      const result = await res.json();
      if (result?.success || res.ok) {
        message.success('Yeni form başarıyla oluşturuldu');
        setCreateFormModalVisible(false);
        createFormForm.resetFields();
        
        setManualFormId(newFormId);
        await loadForm(newFormId);
        
        if (window.history && window.history.pushState) {
          const newUrl = `${newFormId}`;
          window.history.pushState({}, '', newUrl);
        }
      } else {
        throw new Error('Form oluşturulamadı');
      }
    } catch (err) {
      console.error('Create form error', err);
      message.error(err.message || 'Form oluşturulamadı');
    } finally {
      setCreatingForm(false);
    }
  };

  const removeElement = (index) => {
    setElements((prev) => prev.filter((_, i) => i !== index).map((el, i) => ({ ...el, sira: i })));
  };

  const save = async () => {
    if (!formId) {
      message.error('URL veya prop ile geçerli bir form id verin (?id=)');
      return;
    }
    setSaving(true);
    try {
      const normalizedElements = elements.map((el, i) => {
        const { uid, ...rest } = el;
        return { ...rest, sira: i };
      });

      const payload = {
        mysql_id: Number(formId),
        ...formMeta,
        elements: normalizedElements,
        ...(interact && Object.keys(interact).length ? { interact } : {}),
      };

      const res = await fetch(`${API_BASE_URL}/form/formlar/${formId}/raw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      
      if (result?.success) {
        message.success('Form kaydedildi');
      } else {
        message.success('Form kaydedildi');
      }
      await loadForm(formId);
    } catch (err) {
      console.error('Save error', err);
      message.error('Kaydetme başarısız: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const elementTypeOptions = useMemo(() => {
    return elementTypes.map((t) => (
      <Option key={t.id} value={t.id}>{t.label || t.key}</Option>
    ));
  }, [elementTypes]);

  const columns = useMemo(() => [
    {
      title: 'Sıra',
      dataIndex: 'sira',
      key: 'sira',
      width: 50,
      render: (_val, _rec, index) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} onClick={() => moveElement(index, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} onClick={() => moveElement(index, 1)} />
        </Space>
      ),
    },
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (_val, _rec, index) => (
        <Input value={elements[index]?.name} onChange={(e) => updateElement(index, 'name', e.target.value)} />
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (_val, _rec, index) => {
        const currentType = elements[index]?.type;
        const typeValue = currentType !== undefined && currentType !== null ? Number(currentType) : undefined;
        
        return (
          <Select 
            style={{ width: '100%' }} 
            value={typeValue}
            onChange={(v) => updateElement(index, 'type', Number(v))}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              trIncludes(option?.children ?? '', input)
            }
            placeholder="Tip seçiniz"
            notFoundContent={elementTypes.length === 0 ? "Yükleniyor..." : "Bulunamadı"}
          >
            {elementTypeOptions}
          </Select>
        );
      },
    },
    {
      title: 'Zorunlu',
      dataIndex: 'required',
      key: 'required',
      width: 80,
      render: (_val, _rec, index) => (
        <Switch checked={!!elements[index]?.required} onChange={(v) => updateElement(index, 'required', v)} />
      ),
    },
    {
      title: 'Placeholder',
      dataIndex: 'placeholder',
      key: 'placeholder',
      width: 140,
      render: (_val, _rec, index) => (
        <Input value={elements[index]?.placeholder} onChange={(e) => updateElement(index, 'placeholder', e.target.value)} />
      ),
    },
    {
      title: 'Min',
      dataIndex: 'min',
      key: 'min',
      width: 80,
      render: (_val, _rec, index) => (
        <InputNumber style={{ width: '100%' }} value={elements[index]?.min} onChange={(v) => updateElement(index, 'min', v)} />
      ),
    },
    {
      title: 'Max',
      dataIndex: 'max',
      key: 'max',
      width: 80,
      render: (_val, _rec, index) => (
        <InputNumber style={{ width: '100%' }} value={elements[index]?.max} onChange={(v) => updateElement(index, 'max', v)} />
      ),
    },
    {
      title: 'Değer',
      dataIndex: 'value',
      key: 'value',
      width: 160,
      render: (_val, _rec, index) => (
        <Input value={elements[index]?.value} onChange={(e) => updateElement(index, 'value', e.target.value)} />
      ),
    },
    {
      title: 'Sorgu',
      dataIndex: 'query',
      key: 'query',
      width: 160,
      render: (_val, _rec, index) => {
        const isYearBasedCostType = elements[index]?.type === 25;
        const isTableType = elements[index]?.type === 11;
        
        return (
          <div>
            <Input 
              value={elements[index]?.query} 
              onChange={(e) => updateElement(index, 'query', e.target.value)}
              placeholder={isYearBasedCostType ? "Opsiyonel (kullanılmıyor)" : (isTableType ? "SQL sorgu" : "SQL sorgu veya yapılandırma")}
              disabled={isYearBasedCostType}
              style={{ width: '100%' }}
            />
            {isYearBasedCostType && (
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>
                Yıl bazlı maliyet için query gerekmez. Modal'da yıl ve maliyet tipleri seçilir.
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Class',
      dataIndex: 'class',
      key: 'class',
      width: 140,
      render: (_val, _rec, index) => (
        <Input value={elements[index]?.class} onChange={(e) => updateElement(index, 'class', e.target.value)} />
      ),
    },
    {
      title: 'Attr',
      dataIndex: 'attr',
      key: 'attr',
      width: 100,
      render: (_val, _rec, index) => (
        <Input value={elements[index]?.attr} onChange={(e) => updateElement(index, 'attr', e.target.value)} />
      ),
    },
    {
      title: 'W',
      dataIndex: 'w',
      key: 'w',
      width: 80,
      render: (_val, _rec, index) => (
        <InputNumber min={1} max={12} style={{ width: '100%' }} value={elements[index]?.w} onChange={(v) => updateElement(index, 'w', v)} />
      ),
    },
    {
      title: 'Default',
      dataIndex: 'default_value',
      key: 'default_value',
      width: 160,
      render: (_val, _rec, index) => (
        <Input value={elements[index]?.default_value} onChange={(e) => updateElement(index, 'default_value', e.target.value)} />
      ),
    },
    {
      title: 'Aktif',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (_val, _rec, index) => (
        <Switch checked={!!elements[index]?.active} onChange={(v) => updateElement(index, 'active', v)} />
      ),
    },
    {
      title: 'Sil',
      key: 'delete',
      fixed: 'right',
      width: 80,
      render: (_val, _rec, index) => (
        <Popconfirm title="Silinsin mi?" onConfirm={() => removeElement(index)}>
          <Button danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ], [elements, elementTypeOptions]);

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row gutter={[16, 16]} align="bottom" wrap={false} style={{ overflowX: 'auto' }}>
              <Col flex="0 0 150px">
                <Text>Form Adı</Text>
                <Input size="small" value={formMeta.form_name} onChange={(e) => handleMetaChange('form_name', e.target.value)} />
              </Col>
              <Col flex="0 0 150px">
                <Text>Anahtar</Text>
                <Input size="small" value={formMeta.name} onChange={(e) => handleMetaChange('name', e.target.value)} />
              </Col>
              <Col flex="0 0 150px">
                <Text>Hedef</Text>
                <Input size="small" value={formMeta.target} onChange={(e) => handleMetaChange('target', e.target.value)} />
              </Col>
              <Col flex="0 0 150px">
                <Text>Hedef Tablo</Text>
                <Input size="small" value={formMeta.table_name} onChange={(e) => handleMetaChange('table_name', e.target.value)} />
              </Col>
              <Col flex="0 0 150px">
                <Text>SubmitText</Text>
                <Input size="small" value={formMeta.submittext} onChange={(e) => handleMetaChange('submittext', e.target.value)} />
              </Col>
              <Col flex="0 0 150px">
                <Text>Function</Text>
                <Select size="small" value={formMeta.function} style={{ width: '100%' }} onChange={(v) => handleMetaChange('function', v)}>
                  <Option value="Insert">Insert</Option>
                  <Option value="Update">Update</Option>
                  <Option value="Delete">Delete</Option>
                </Select>
              </Col>
              <Col flex="0 0 150px">
                <Text>Yönlendirme</Text>
                <Input size="small" value={formMeta.redirect} onChange={(e) => handleMetaChange('redirect', e.target.value)} />
              </Col>
              <Col flex="0 0 150px">
                <Text>Görünüm</Text>
                <Select size="small" value={formMeta.view} style={{ width: '100%' }} onChange={(v) => handleMetaChange('view', v)}>
                  <Option value={0}>Tam Ekran</Option>
                  <Option value={1}>Yarım Görünüm</Option>
                </Select>
              </Col>
              <Col flex="0 0 150px">
                <Text>Yetki</Text>
                <Input size="small" value={formMeta.yetki} onChange={(e) => handleMetaChange('yetki', e.target.value)} />
              </Col>
            </Row>
            <Divider />
            <Space>
              <Button type="primary" icon={<PlusCircleOutlined />} onClick={addFormElement}>Yeni Form Oluştur</Button>
              <Button icon={<PlusOutlined />} onClick={addElement}>Yeni Eleman Ekle</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving}>Kaydet</Button>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Form Elemanları">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : (
          <Table
                size="small"
                dataSource={elements}
                columns={columns}
                rowKey={(rec, idx) => idx}
                pagination={false}
                scroll={{ x: 'max-content' }}
                className={darkMode ? 'dark-table' : ''}
              />
            )}
          </Card>
        </Col>
      </Row>
      <Modal
        title="Yeni Form Oluştur"
        open={createFormModalVisible}
        onOk={createNewForm}
        onCancel={() => {
          setCreateFormModalVisible(false);
          createFormForm.resetFields();
        }}
        confirmLoading={creatingForm}
        okText="Oluştur"
        cancelText="İptal"
        width={600}
      >
        <Form
          form={createFormForm}
          layout="vertical"
          initialValues={{
            submittext: 'Kaydet',
            function: 'Insert',
            view: 1,
            target: 'formHandle',
          }}
        >
          <Form.Item
            label="MySQL ID"
            name="mysql_id"
            rules={[
              { required: true, message: 'MySQL ID gereklidir' },
              { type: 'number', message: 'Geçerli bir sayı giriniz' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Örn: 331"
              min={1}
            />
          </Form.Item>

          <Form.Item
            label="Form Adı"
            name="form_name"
            rules={[{ required: true, message: 'Form adı gereklidir' }]}
          >
            <Input placeholder="Örn: Platform" />
          </Form.Item>

          <Form.Item
            label="Anahtar (name)"
            name="name"
            rules={[{ required: true, message: 'Anahtar gereklidir' }]}
          >
            <Input placeholder="Örn: platform-depo" />
          </Form.Item>

          <Form.Item
            label="Hedef (target)"
            name="target"
          >
            <Input placeholder="Örn: formHandle" />
          </Form.Item>

          <Form.Item
            label="Hedef Tablo"
            name="table_name"
          >
            <Input placeholder="Örn: platformlar" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Submit Text"
                name="submittext"
              >
                <Input placeholder="Kaydet" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Function"
                name="function"
              >
                <Select>
                  <Option value="Insert">Insert</Option>
                  <Option value="Update">Update</Option>
                  <Option value="Delete">Delete</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Yönlendirme"
            name="redirect"
          >
            <Input placeholder="Yönlendirme URL'i (opsiyonel)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Görünüm"
                name="view"
              >
                <Select>
                  <Option value={0}>Tam Ekran</Option>
                  <Option value={1}>Yarım Görünüm</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Yetki"
                name="yetki"
              >
                <Input placeholder="Yetki (opsiyonel)" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default FormManager;